import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Button, IconButton, InputLabel, Stack } from "@mui/material";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";
import { FormCheckbox } from "UI/FormCheckbox";
import { FormMaskedTextField } from "UI/FormMaskedTextField";
import { FormTextField } from "UI/FormTextField";
import {
  steps as registerSteps,
  usePublicSignUpSender,
} from "hooks/usePublicSignUpSender";
import {
  emailValidation,
  getPasswordConfirmValidation,
  nameValidation,
  optionalPasswordValidation,
  phoneNumberValidation,
} from "models/validations";
import { useContext } from "react";
import { Link } from "react-router-dom";
import { GlobalContext } from "store/globalContext";
import * as FP from "utils/fp-js";
import { Captcha } from "./Captcha";
import { SimplifiedSignUpSenderButtonDone } from "./SimplifiedSignUpSenderButtonDone";

export const SimplifiedSignUpSender = () => {
  const { userData } = useContext(GlobalContext);

  const {
    isLoading,
    step,
    control,
    handleSubmit,
    onSubmit,
    isAgreeToTerms,
    fullScreen,
    isSignUpDone,
    setValue,
    steps,
    isPasswordVisible,
    password,
    setIsPasswordVisible,
  } = usePublicSignUpSender({
    defaultValues: {
      firstName: userData.fullName.split(" ")[0],
      lastName: userData.fullName.split(" ")[1],
      phone: userData?.phone ?? "",
      email: userData?.email ?? "",
      isAgreeToTerms: true,
      code: "",
      captchaId: "",
      recipientId: "",
      practiceId: userData?.organization?.id ?? "",
      originatorId: "",
      password: null,
      password_repeat: null,
    },
    startStep: registerSteps.CREATE_SENDER,
  });

  if (isSignUpDone) {
    return <SimplifiedSignUpSenderButtonDone />;
  }

  return (
    <CommonOutletTemplate noPadding title="Sender Registration">
      <div className="mt-10 flex flex-col items-center">
        <div className="flex flex-row justify-center items-center">
          <div className="text-2xl sm:text-2xl text-center font-bold mr-4 max-w-sm">
            Become a Referral Sender
          </div>
        </div>
        <div className="text-center mt-2 flex flex-col gap-3 max-w-md">
          {FP.match(step)
            .on(
              steps.CREATE_SENDER,
              <div className="text-gray-500 text-sm">
                Enter your personal information to register as a Sender in the
                Referral Stream Button Application.
              </div>
            )
            .otherwise(null)}
        </div>
        <div className="flex flex-col justify-center items-center gap-4 w-full">
          <div className="bg-white mt-2 p-4 sm:p-8 sm:pt-6 rounded-2xl flex flex-col justify-center items-stretch gap-2 sm:gap-4 max-w-3xl relative">
            <form
              className="flex flex-col sm:flex-row rounded-xl gap-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              {FP.match(step)
                .on(
                  steps.CREATE_SENDER,
                  <Stack direction={fullScreen ? "column" : "row"} gap={4}>
                    <Stack
                      width={fullScreen ? "100%" : "calc(50% - 1rem)"}
                      gap={1}
                    >
                      <Stack direction={fullScreen ? "column" : "row"} gap={1}>
                        <Stack alignItems={"flex-start"}>
                          <InputLabel htmlFor="firstName">
                            First name{" "}
                          </InputLabel>
                          <FormTextField
                            id="firstName"
                            name="firstName"
                            control={control}
                            rules={nameValidation}
                            InputProps={{
                              autoComplete: "off",
                              disabled: isLoading,
                            }}
                          />
                        </Stack>
                        <Stack alignItems={"flex-start"}>
                          <InputLabel htmlFor="lastName">Last name</InputLabel>
                          <FormTextField
                            id="lastName"
                            name="lastName"
                            control={control}
                            rules={nameValidation}
                            InputProps={{
                              autoComplete: "off",
                              disabled: isLoading,
                            }}
                          />
                        </Stack>
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="email">Email</InputLabel>
                        <FormTextField
                          id="email"
                          name="email"
                          control={control}
                          rules={emailValidation}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="phone">Phone</InputLabel>
                        <FormMaskedTextField
                          id="phone"
                          name="phone"
                          control={control}
                          rules={phoneNumberValidation}
                          mask="999-999-9999"
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="jobTitle">
                          Job Title (optional)
                        </InputLabel>
                        <FormTextField
                          id="jobTitle"
                          name="jobTitle"
                          control={control}
                          InputProps={{
                            autoComplete: "off",
                            disabled: isLoading,
                          }}
                        />
                      </Stack>
                    </Stack>
                    <Stack
                      width={fullScreen ? "100%" : "calc(50% - 1rem)"}
                      gap={1}
                    >
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="password">
                          Create password (optional)
                        </InputLabel>
                        <FormTextField
                          id="password"
                          name="password"
                          control={control}
                          rules={optionalPasswordValidation}
                          type={isPasswordVisible ? "text" : "password"}
                          InputProps={{
                            autoComplete: "new-password",
                          }}
                          inputProps={{
                            autoComplete: "new-password",
                          }}
                        />
                      </Stack>
                      <Stack alignItems={"flex-start"}>
                        <InputLabel htmlFor="password_repeat">
                          Repeat password
                        </InputLabel>
                        <FormTextField
                          id="password_repeat"
                          name="password_repeat"
                          control={control}
                          rules={getPasswordConfirmValidation(password.current)}
                          type={isPasswordVisible ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                color="info"
                                onClick={() =>
                                  setIsPasswordVisible(!isPasswordVisible)
                                }
                              >
                                {isPasswordVisible ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            ),
                            autoComplete: "off",
                          }}
                          inputProps={{
                            autoComplete: "off",
                          }}
                        />
                      </Stack>
                      <Captcha control={control} setValue={setValue} />
                      <Stack alignItems={"flex-start"}>
                        <FormCheckbox
                          name="isAgreeToTerms"
                          control={control}
                          label={
                            <div>
                              I agree to{" "}
                              <Link
                                to="https://referral-stream.webflow.io/terms-of-service"
                                target="_blank"
                              >
                                Terms & Conditions
                              </Link>
                            </div>
                          }
                          disabled={isLoading}
                        />
                      </Stack>
                      <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isLoading || !isAgreeToTerms}
                      >
                        Start Sending Referrals
                      </Button>
                    </Stack>
                  </Stack>
                )
                .otherwise(null)}
            </form>
          </div>
        </div>
      </div>
    </CommonOutletTemplate>
  );
};
